import React, { memo } from 'react';
import PropTypes from 'prop-types';

import AlertModal from './AlertModal';
import Notification from './Notification';

import styles from './alert.module.css';

const Alert = ( {
	action,
	actionText = '',
	closeModal,
	closeText = '',
	heading = '',
	message = '',
	name,
	open,
	type = '',
	...props
} ) => {
	switch ( type ) {
		case 'confirm':
			return (
				<AlertModal
					action={ action }
					actionColor="pink"
					actionText={ actionText || 'Confirm' }
					closeModal={ closeModal }
					closeText={ closeText || 'Cancel' }
					id={ name }
					message={ message }
					open={ open }
					{ ...props }
				/>
			);

		case 'delete':
			return (
				<AlertModal
					action={ action }
					actionColor="red"
					actionText={ actionText || 'Delete' }
					closeModal={ closeModal }
					closeText={ closeText || 'Cancel' }
					id={ name }
					message={ message }
					open={ open }
					{ ...props }
				/>
			);

		case 'warning':
			return (
				<AlertModal
					action={ action }
					actionColor="yellow"
					actionText={ actionText || 'Continue' }
					closeModal={ closeModal }
					closeText={ closeText || ( action
						? 'Cancel'
						: 'Close' )
					}
					heading={ heading || 'Warning' }
					icon="exclamation triangle"
					iconColor="yellow"
					id={ name }
					message={ message }
					open={ open }
					{ ...props }
				/>
			);

		case 'success':
			return (
				<AlertModal
					closeModal={ closeModal }
					closeText={ closeText || 'Close' }
					heading={ heading || 'Success' }
					icon="check circle"
					iconColor="green"
					id={ name }
					message={ message }
					open={ open }
					{ ...props }
				/>
			);

		case 'error':
			return (
				<AlertModal
					closeModal={ closeModal }
					closeText={ closeText || 'Close' }
					heading={ heading || 'Error' }
					icon="exclamation circle"
					iconColor="red"
					id={ name }
					message={ message || 'An error has occurred and we were unable to process your request. Please refresh your browser and try again. If the error persists, please contact support.' }
					open={ open }
					{ ...props }
					className={ styles.shake }
				/>
			);

		case 'info':
			return (
				<AlertModal
					closeModal={ closeModal }
					closeText={ closeText || 'Close' }
					heading={ heading || 'Info' }
					icon="info circle"
					id={ name }
					message={ message }
					open={ open }
					{ ...props }
				/>
			);

		case 'notification':
			return (
				<Notification
					closeModal={ closeModal }
					id={ name }
					message={ message }
					open={ open }
					{ ...props }
				/>
			);

		default:
			return (
				<AlertModal
					closeModal={ closeModal }
					hideActions
					id={ name }
					message={ message }
					open={ open }
					{ ...props }
				/>
			);
	}
};

Alert.propTypes = {
	// action is required for confirm and delete alerts
	action: ( props, propName, componentName ) => {
		if ( props.type === ( 'confirm' || 'delete' ) && typeof props[ propName ] !== 'function' ) {
			return new Error( `Failed prop type: The prop \`${ propName }\` is marked as required in \`${ componentName }\`, but its value is \`${ props[ propName ] }\`.` );
		}

		return null;
	},
	actionText: PropTypes.string,
	closeModal: PropTypes.func.isRequired,
	closeText: PropTypes.string,
	heading: PropTypes.string,
	message: PropTypes.node,
	name: PropTypes.string.isRequired,
	open: PropTypes.bool.isRequired,
	type: PropTypes.oneOf( [
		'confirm',
		'delete',
		'warning',
		'error',
		'success',
		'info',
		'notification'
	] )
};

export default memo( Alert );
